import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  CrunchData,
  DocumentSvcCrunches,
} from '@dc-shared-web/hooks/api/document/types'
import {
  DOCUMENTS_QUERY_KEY,
  LIST_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import {
  CrunchesPageEntities,
  shouldInvalidateCrunchesPage,
} from '@dc-shared-web/hooks/utils/shouldInvalidateCrunchesPage'

type MoveDocumentToProjectParams = {
  documentId: string
  projectId?: number
}

const moveDocumentToProject = async (
  apiClient: AxiosInstance,
  params: MoveDocumentToProjectParams
) => {
  const { data } = await apiClient.patch(
    `/ajax/crunches/${params.documentId}/project-info`,
    {
      projectId: params.projectId,
    }
  )

  return data.data
}

export const useMoveDocumentToProject = (params?: {
  onSuccess: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: MoveDocumentToProjectParams) =>
      moveDocumentToProject(apiClient, mutationParams),
    {
      onSuccess: async (_, context: MoveDocumentToProjectParams) => {
        await Promise.allSettled([
          queryClient.invalidateQueries<CrunchesPageEntities>({
            queryKey: [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY],
            refetchPage: (page) => {
              return shouldInvalidateCrunchesPage(page, context.documentId)
            },
          }),
          queryClient.invalidateQueries<CrunchData[]>({
            queryKey: ['Document', context.documentId],
          }),
          queryClient.invalidateQueries<DocumentSvcCrunches>({
            queryKey: ['DocumentSvcCrunches', context.documentId],
          }),
        ])

        toast({
          variant: 'success',
          title: 'Successfully moved document',
        })

        params?.onSuccess()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error moving document',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
